import { MenuButton, MenuItem, TextInput } from '@carbon/react';
import { asProblem, detailRoute, FormModal, hasPermission, problemMessage, useAdmin } from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { hoApi } from '../api';

/**
 * *Create from template* on the Roles list (ADR 0116): a role for a common job (accounting, marketing, area or store manager,
 * cashier, kitchen) with its permissions and limits, under a code and name of your own, then opened to adjust.
 */
export function RoleTemplateButton() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<string>();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const templates = useQuery({
    queryKey: ['ho', 'role-templates'],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/role-templates');
      if (!data) throw error;
      return data;
    },
    enabled: hasPermission(session, 'ho.org.roles.manage'),
  });

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/identity/roles/from-template', {
        body: { code: code.trim(), name: name.trim(), template: template ?? null },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (role) => {
      setTemplate(undefined);
      void navigate(detailRoute('org.role', String(role.id)));
    },
  });

  if (!hasPermission(session, 'ho.org.roles.manage') || !templates.data?.length) return null;

  const title = (key: string) => t(`roleTemplates.${key}` as I18nKey, { defaultValue: key });
  const problem = create.error ? (asProblem(create.error) as ApiProblem) : null;
  const fieldError = (key: 'code' | 'name') =>
    Object.entries(problem?.errors ?? {}).find(([field]) => field.toLowerCase() === key)?.[1]?.[0] ??
    (problem?.field === key ? problemMessage(t, problem) : undefined);

  return (
    <>
      <MenuButton label={t('roleTemplates.action')} kind="ghost" size="lg" menuAlignment="bottom-end">
        {templates.data.map((tpl) => (
          <MenuItem
            key={tpl.key}
            label={title(tpl.key)}
            onClick={() => {
              setTemplate(tpl.key);
              setCode(tpl.key.toUpperCase());
              setName(title(tpl.key));
              create.reset();
            }}
          />
        ))}
      </MenuButton>
      {template && (
        <FormModal
          title={t('roleTemplates.title', { template: title(template) })}
          label={t('roleTemplates.action')}
          size="sm"
          primaryText={t('admin.create')}
          pending={create.isPending}
          error={problem && !problem.errors && !problem.field ? problemMessage(t, problem) : null}
          onSubmit={() => create.mutate()}
          onClose={() => setTemplate(undefined)}
        >
          <p>{t('roleTemplates.hint')}</p>
          <TextInput
            id="role-template-code"
            labelText={t('md.fields.code')}
            required
            maxLength={50}
            value={code}
            invalid={Boolean(fieldError('code'))}
            invalidText={fieldError('code')}
            onChange={(e) => setCode(e.target.value)}
          />
          <TextInput
            id="role-template-name"
            labelText={t('md.fields.name')}
            required
            value={name}
            invalid={Boolean(fieldError('name'))}
            invalidText={fieldError('name')}
            onChange={(e) => setName(e.target.value)}
          />
        </FormModal>
      )}
    </>
  );
}
